// RoleBasedRoute.jsx — matches backend permissions.py exactly
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

// Role rank constants — MUST stay in sync with backend permissions.py ROLE_RANKS
export const ROLE_RANKS = {
  // Global Admin
  'ADMIN': 10,

  // Department Heads — Rank 9
  'OPS_HEAD_ADMIN': 9,
  'HR_HEAD': 9,
  'SALES_HEAD': 9,
  'TECH_ADMIN': 9,
  'CHIEF_DEVELOPMENT_HEAD': 9,
  'FL_ADMIN': 9,

  // Managers — Rank 7
  'MANAGER': 7,
  'HR_MANAGER': 7,
  'TECH_MANAGER': 7,
  'DEVELOPMENT_MANAGER': 7,
  'FLOOR_SUPERVISOR': 7,

  // Team Managers / Coordinators — Rank 5
  'TEAM_MANAGER': 5,
  'HR_COORDINATOR': 5,
  'FLOOR_COORDINATOR': 5,

  // Executives / Assistants — Rank 3
  'SALES_EXECUTIVE': 3,
  'TECH_EXECUTIVE': 3,
  'FRONTEND_BACKEND_EXECUTIVE': 3,
  'PAYROLL_ASSISTANT': 3,
  'TECH_ASSISTANT': 3,
  'FRONTEND_BACKEND_ASSISTANT': 3,
  'OFFICE_ASSISTANT': 3,

  // Sales Assistant — Rank 2
  'SALES_ASSISTANT': 2,

  // Common Roles
  'SPA': 2,
  'PA': 1,
};

// HR roles — Level-2 leave approvers
export const HR_ROLES = ['HR_HEAD', 'HR_MANAGER', 'HR_COORDINATOR'];

// ─── Core helpers ─────────────────────────────────────────────────────────────

export const getUserRank = (user) => {
  if (!user?.employee_profile) return 0;
  return ROLE_RANKS[user.employee_profile.role] ?? 0;
};

export const getUserRole = (user) => {
  return user?.employee_profile?.role ?? null;
};

// ─── Boolean role checks ──────────────────────────────────────────────────────

/** ADMIN only (rank 10) */
export const isAdmin = (user) => getUserRole(user) === 'ADMIN';

/** HR roles: HR_HEAD, HR_MANAGER, HR_COORDINATOR */
export const isHRRole = (user) => {
  const role = getUserRole(user);
  return role ? HR_ROLES.includes(role) : false;
};

/** ADMIN or any HR role */
export const isAdminOrHR = (user) => isAdmin(user) || isHRRole(user);

/** Department Head level or above (rank ≥ 9) */
export const isDeptHeadOrAbove = (user) => getUserRank(user) >= 9;

/** Manager level or above (rank ≥ 7) */
export const isManagerOrAbove = (user) => getUserRank(user) >= 7;

/** Team Manager / Coordinator level or above (rank ≥ 5) */
export const isTeamManagerOrAbove = (user) => getUserRank(user) >= 5;

/** Above PA level (rank > 1) */
export const isAbovePA = (user) => getUserRank(user) > 1;

// ─── Feature-specific permission helpers ─────────────────────────────────────

/**
 * Can approve leaves as HR (Level 2)?
 * Yes for: ADMIN, HR_HEAD, HR_MANAGER, HR_COORDINATOR
 */
export const canApproveLeavesAsHR = (user) => isAdminOrHR(user);

/**
 * Can see the leave management page (Level 1 approval)?
 * Yes for: rank ≥ 5 (Team Manager and above) + HR + Admin
 * Note: backend additionally enforces same-dept + outrank checks per-leave.
 */
export const canManageLeaves = (user) => isTeamManagerOrAbove(user);

/**
 * Can VIEW another employee's attendance records?
 * Yes for: rank ≥ 5 in same department with higher rank, or ADMIN/HR
 * Note: backend enforces the same-dept + outrank conditions; the frontend
 * only gates whether the UI is shown at all.
 */
export const canViewAttendance = (user) => isTeamManagerOrAbove(user) || isAdminOrHR(user);

/**
 * Can UPDATE attendance status or punch times?
 * Yes for: ADMIN and HR ONLY (matches backend update-status restriction).
 * Team Managers (rank 5–8) can VIEW but never edit.
 */
export const canUpdateAttendance = (user) => isAdminOrHR(user);

// ─── Route Guard Component ────────────────────────────────────────────────────

/**
 * RoleBasedRoute — protect routes by rank, adminOnly flag, or a custom check.
 *
 * Props:
 *   minRank    {number}   — user rank must be >= this value
 *   adminOnly  {boolean}  — only ADMIN (rank 10) allowed
 *   roleCheck  {function} — custom (user) => bool predicate; evaluated first
 *   redirectTo {string}   — where to send unauthorised users (default "/")
 */
const RoleBasedRoute = ({
  children,
  minRank,
  adminOnly = false,
  roleCheck,
  redirectTo = '/',
}) => {
  const user = useSelector((state) => state.auth.user);

  if (!user) return <Navigate to="/login" replace />;

  // Custom predicate — most flexible, checked first
  if (roleCheck) {
    if (!roleCheck(user)) return <Navigate to={redirectTo} replace />;
    return children;
  }

  // Admin-only shortcut
  if (adminOnly && !isAdmin(user)) return <Navigate to={redirectTo} replace />;

  // Minimum rank check
  if (minRank && getUserRank(user) < minRank) return <Navigate to={redirectTo} replace />;

  return children;
};

export default RoleBasedRoute;